/**
 * Reusable Export Dropdown Component
 * shadcn/Tailwind implementation
 */

import React from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from '../ui/use-toast';
import { Download, ChevronDown, FileSpreadsheet, FileText, FileCode, FileDown } from 'lucide-react';

export type ExportFormat = 'excel' | 'csv' | 'json' | 'pdf';

export interface ExportOptions {
  data: any[];
  filename?: string;
  formats?: ExportFormat[];
  onExportStart?: (format: ExportFormat) => void;
  onExportComplete?: (format: ExportFormat, recordCount: number) => void;
  onExportError?: (error: Error) => void;
  customExportHandler?: (format: ExportFormat, data: any[]) => Promise<void>;
}

export interface ExportDropdownProps {
  options: ExportOptions;
  buttonText?: string;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  disabled?: boolean;
  className?: string;
}

const DEFAULT_FORMATS: ExportFormat[] = ['excel', 'csv', 'pdf', 'json'];

const FORMAT_CONFIG: Record<ExportFormat, { label: string; icon: React.ReactNode; description: string }> = {
  excel: { label: 'Export as Excel', icon: <FileSpreadsheet className="h-4 w-4" />, description: '.xlsx' },
  csv: { label: 'Export as CSV', icon: <FileText className="h-4 w-4" />, description: '.csv' },
  json: { label: 'Export as JSON', icon: <FileCode className="h-4 w-4" />, description: '.json' },
  pdf: { label: 'Export as PDF', icon: <FileDown className="h-4 w-4" />, description: '.pdf' },
};

/**
 * Fallback for callers that pass no `customExportHandler`: derive the columns
 * from the records themselves and render them through the shared styled
 * exporter, so every format — PDF included — comes out branded and formatted.
 */
const defaultExportHandler = async (format: ExportFormat, data: any[], filename: string = 'export') => {
  const { exportRich } = await import('../../utils/richExport');

  // Scalar fields only: a nested object has no sensible column of its own.
  const keys = Array.from(
    data.reduce((set: Set<string>, rec) => {
      Object.entries(rec ?? {}).forEach(([k, v]) => {
        if (v === null || typeof v !== 'object') set.add(k);
      });
      return set;
    }, new Set<string>()),
  );
  const label = (k: string) => k.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  await exportRich(format, {
    fileName: filename,
    title: label(filename.replace(/-export$/, '')),
    columns: keys.map(label),
    rows: data.map((rec) => keys.map((k) => (typeof rec?.[k] === 'boolean' ? (rec[k] ? 'Yes' : 'No') : rec?.[k]))),
  });

  return { format, filename, recordCount: data.length };
};

export const ExportDropdown: React.FC<ExportDropdownProps> = ({
  options, buttonText = 'Export', size = 'default', variant = 'outline', disabled = false, className,
}) => {
  const { data, filename = 'export', formats = DEFAULT_FORMATS, onExportStart, onExportComplete, onExportError, customExportHandler } = options;

  const handleExport = async (format: ExportFormat) => {
    try {
      if (data.length === 0) {
        toast({ title: 'No data', description: 'No data available for export', variant: 'destructive' });
        return;
      }
      onExportStart?.(format);
      if (customExportHandler) {
        await customExportHandler(format, data);
      } else {
        const result = await defaultExportHandler(format, data, filename);
        onExportComplete?.(format, result.recordCount);
      }
      toast({ title: 'Export complete', description: `${format.toUpperCase()} export completed — ${data.length} records`, variant: 'success' });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      onExportError?.(error instanceof Error ? error : new Error(msg));
      toast({ title: 'Export failed', description: msg, variant: 'destructive' });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={disabled} className={className}>
          <Download className="h-4 w-4 mr-2" />
          {buttonText}
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {formats.map((format) => (
          <DropdownMenuItem key={format} onClick={() => handleExport(format)}>
            {FORMAT_CONFIG[format].icon}
            <span className="ml-2">{FORMAT_CONFIG[format].label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export interface BulkExportDropdownProps extends Omit<ExportDropdownProps, 'options'> {
  allData: any[];
  selectedKeys: string[];
  filename?: string;
  formats?: ExportFormat[];
  onExportStart?: (format: ExportFormat) => void;
  onExportComplete?: (format: ExportFormat, recordCount: number) => void;
  onExportError?: (error: Error) => void;
  customExportHandler?: (format: ExportFormat, data: any[]) => Promise<void>;
  onSelectionClear?: () => void;
}

export const BulkExportDropdown: React.FC<BulkExportDropdownProps> = ({
  allData, selectedKeys, filename = 'selected-export', formats = ['excel', 'csv'],
  onExportStart, onExportComplete, onExportError, customExportHandler, onSelectionClear,
  buttonText = 'Export Selected', ...props
}) => {
  const selectedData = allData.filter((item) => selectedKeys.includes(item.key || item.id || String(item)));

  const handleBulkExportComplete = (format: ExportFormat, recordCount: number) => {
    onExportComplete?.(format, recordCount);
    onSelectionClear?.();
  };

  return (
    <ExportDropdown
      {...props}
      options={{ data: selectedData, filename, formats, onExportStart, onExportComplete: handleBulkExportComplete, onExportError, customExportHandler }}
      buttonText={buttonText}
      disabled={selectedKeys.length === 0}
    />
  );
};

export default ExportDropdown;
